<script setup>
import Layout from "../../layouts/main.vue";
import { computed, onMounted, ref, watch } from "vue";
import toastr from "toastr";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";
import {
  fetchTypeWeightSummaryAsync,
  updateTypeWeightSummaryAsync,
  resetTypeWeightSummaryAsync,
  fetchInfoConfigUserCategoryAsync,
  updateInfoConfigUserCategoryAsync,
  resetInfoConfigUserCategoryAsync,
  fetchInfoConfigThreshGeneralAsync,
  updateInfoConfigThreshGeneralAsync,
  resetInfoConfigThreshGeneralAsync,
  fetchQoeWeightConfigAsync,
  updateQoeWeightConfigAsync,
  resetQoeWeightConfigAsync,
} from "./api";

const { t } = useI18n();

const threshUserCategoryData = ref([]);
const userCategoryData = ref([]);
const speedTestData = ref([]);
const usageTimeData = ref([]);
const trafficInterfaceData = ref([]);
const qoeTableData = ref([]);
const qoeTotal = ref(0);
const qoePage = ref(1);
const qoePageSize = ref(10);
const showEditDialog = ref(false);
const editingItem = ref({});

watch([qoePage, qoePageSize], () => {
  fetchQoeWeightConfig();
});

const qoeOffset = computed(() => (qoePage.value - 1) * qoePageSize.value);

const fetchthreshUserCategoryData = async () => {
  try {
    const [data] = await fetchTypeWeightSummaryAsync();
    threshUserCategoryData.value = data.data;
  } catch (e) {
    console.error("Error: ", e);
  }
};

const fetchInfoConfigUserCategory = async () => {
  try {
    userCategoryData.value = await fetchInfoConfigUserCategoryAsync();
  } catch (e) {
    console.error("Error: ", e);
  }
};

const fetchInfoConfigThreshGeneralBySpeedtest = async () => {
  try {
    speedTestData.value = await fetchInfoConfigThreshGeneralAsync("speedtest");
  } catch (e) {
    console.error("Error: ", e);
  }
};

const fetchInfoConfigThreshGeneralByCustSpeedAlert = async () => {
  try {
    usageTimeData.value = await fetchInfoConfigThreshGeneralAsync(
      "cust_speed_alert",
    );
  } catch (e) {
    console.error("Error: ", e);
  }
};

const fetchInfoConfigThreshGeneralByInterfaceSpeed = async () => {
  try {
    trafficInterfaceData.value = await fetchInfoConfigThreshGeneralAsync(
      "interface speed",
    );
  } catch (e) {
    console.error("Error: ", e);
  }
};

const fetchQoeWeightConfig = async () => {
  try {
    const offset = (qoePage.value - 1) * qoePageSize.value;
    const [data, meta] = await fetchQoeWeightConfigAsync(
      qoePageSize.value,
      offset,
    );
    qoeTableData.value = data.data;
    qoeTotal.value = meta.meta.total;
  } catch (e) {
    console.error("Error: ", e);
  }
};

const openEditDialog = (item, type_table, tableTitle) => {
  editingItem.value = { ...item, type_table, tableTitle };
  showEditDialog.value = true;
  console.log(editingItem.value, item);
};

const isOutOfRange = (val, min, max) => {
  if (min == null || max == null) return false;
  return Number(val) < Number(min) || Number(val) > Number(max);
};

const isMinGreaterThanMax = (val1, val2) => {
  if (val1 == null || val2 == null) return false;
  return Number(val1) > Number(val2);
};

const isNaNValue = (val) => {
  if (val === null || val === undefined || val === "") return true;
  return isNaN(Number(val));
};

const updateConfigMap = {
  thresh: {
    buildPayload: (item) => ({ id: item.label, weight: item.value }),
    updateFn: updateTypeWeightSummaryAsync,
    refreshFn: fetchthreshUserCategoryData,
  },
  user: {
    buildPayload: (item) => ({
      id: `${item.type}:${item.label}`,
      thresh_lower: item.value1,
      thresh_higher: item.value2,
    }),
    updateFn: updateInfoConfigUserCategoryAsync,
    refreshFn: fetchInfoConfigUserCategory,
  },
  speed: {
    buildPayload: (item) => ({
      id: `${item.type}:${item.label}`,
      value: item.value,
      type: "speedtest",
    }),
    updateFn: updateInfoConfigThreshGeneralAsync,
    refreshFn: fetchInfoConfigThreshGeneralBySpeedtest,
  },
  usage: {
    buildPayload: (item) => ({
      id: `${item.type}:${item.label}`,
      value: item.value,
      type: "cust_speed_alert",
    }),
    updateFn: updateInfoConfigThreshGeneralAsync,
    refreshFn: fetchInfoConfigThreshGeneralByCustSpeedAlert,
  },
  traffic: {
    buildPayload: (item) => ({
      id: `${item.type}:${item.label}`,
      value: item.value,
      type: "interface speed",
    }),
    updateFn: updateInfoConfigThreshGeneralAsync,
    refreshFn: fetchInfoConfigThreshGeneralByInterfaceSpeed,
  },
  qoe: {
    buildPayload: (item) => ({ id: item.service_id, weight: item.value }),
    updateFn: updateQoeWeightConfigAsync,
    refreshFn: fetchQoeWeightConfig,
  },
};

const handleUpdateCategory = async () => {
  const item = editingItem.value;
  const { min, max, value, value1, value2 } = item;

  if (
    (item.type_table === "user" &&
      (isNaNValue(value1) || isNaNValue(value2))) ||
    (item.type_table !== "user" && isNaNValue(value))
  ) {
    toastr.error(t("t_validate_number_only"));
    return;
  }

  if (
    isOutOfRange(value, min, max) ||
    isOutOfRange(value1, min, max) ||
    isOutOfRange(value2, min, max) ||
    isMinGreaterThanMax(value1, value2)
  ) {
    return;
  }

  const config = updateConfigMap[item.type_table];
  if (!config) return;

  const payload = config.buildPayload(item);
  await config.updateFn(payload);
  await config.refreshFn();
  toastr.success(t("toastr_update_success"));
  showEditDialog.value = false;
};

const resetConfigMap = {
  thresh: {
    buildId: (row) => row.type,
    resetFn: resetTypeWeightSummaryAsync,
    refreshFn: fetchthreshUserCategoryData,
  },
  user: {
    buildId: (row) => `${row.type}:${row.user_type}`,
    resetFn: resetInfoConfigUserCategoryAsync,
    refreshFn: fetchInfoConfigUserCategory,
  },
  speed: {
    buildId: (row) => `${row.type}:${row.name}`,
    resetFn: resetInfoConfigThreshGeneralAsync,
    refreshFn: fetchInfoConfigThreshGeneralBySpeedtest,
  },
  usage: {
    buildId: (row) => `${row.type}:${row.name}`,
    resetFn: resetInfoConfigThreshGeneralAsync,
    refreshFn: fetchInfoConfigThreshGeneralByCustSpeedAlert,
  },
  traffic: {
    buildId: (row) => `${row.type}:${row.name}`,
    resetFn: resetInfoConfigThreshGeneralAsync,
    refreshFn: fetchInfoConfigThreshGeneralByInterfaceSpeed,
  },
  qoe: {
    buildId: (row) => row.service_id,
    resetFn: resetQoeWeightConfigAsync,
    refreshFn: fetchQoeWeightConfig,
  },
};

const handleResetRow = async (row, typeTable) => {
  console.log("row", row);
  const config = resetConfigMap[typeTable];
  if (!config) return;

  const { isConfirmed } = await Swal.fire({
    title: t("t_warning"),
    text: t("t_confirm_reset_to_default"),
    icon: "warning",
    confirmButtonColor: "#f46a6a",
    confirmButtonText: t("swal_accept"),
    cancelButtonText: t("swal_cancel"),
    showCancelButton: true,
    allowOutsideClick: false,
  });
  if (!isConfirmed) return;

  try {
    const id = config.buildId(row);
    await config.resetFn(id);
    await config.refreshFn();
    toastr.success(t("toastr_update_success"));
  } catch (e) {
    if (!e?.handled) {
      toastr.error(t("toastr_error"));
    }
  }
};

onMounted(async () => {
  await Promise.all([
    fetchthreshUserCategoryData(),
    fetchInfoConfigUserCategory(),
    fetchInfoConfigThreshGeneralBySpeedtest(),
    fetchInfoConfigThreshGeneralByCustSpeedAlert(),
    fetchInfoConfigThreshGeneralByInterfaceSpeed(),
    fetchQoeWeightConfig(),
  ]);
});
</script>

<template>
  <Layout>
    <div class="config-container">
      <div class="config-content-wrapper">
        <div class="tab-card-content mb-4">
          <div class="tab-full-title">
            <i class="ri-settings-4-line align-bottom me-1"></i>
            {{ $t("t_clickhouse_threshold_category") }}
          </div>

          <div class="section-subtitle mt-3">
            {{ $t("t_clickhouse_threshold_type_weights") }}
          </div>
          <div class="table-wrapper">
            <el-table
              :data="threshUserCategoryData"
              border
              style="width: 100%"
              class="modern-table"
            >
              <el-table-column
                :label="$t('t-stt')"
                type="index"
                width="60"
                align="center"
              />
              <el-table-column
                :label="$t('t-type')"
                min-width="150"
                align="center"
              >
                <template #default="scope">
                  <div class="col-tooltip">{{ scope.row.type }}</div>
                </template>
              </el-table-column>
              <el-table-column
                prop="weight"
                :label="$t('t_weight')"
                width="120"
                align="center"
              />
              <el-table-column
                :label="$t('t_description_column')"
                min-width="300"
                show-overflow-tooltip
                align="center"
              >
                <template #default="scope">
                  <div class="col-tooltip">{{ scope.row.description }}</div>
                </template>
              </el-table-column>
              <el-table-column
                :label="$t('t-action')"
                width="120"
                align="center"
              >
                <template #default="scope">
                  <div class="btn-group-thao-tac">
                    <el-tooltip
                      effect="light"
                      :content="$t('action_option')"
                      placement="top"
                    >
                      <button
                        type="button"
                        class="btn btn-soft-success waves-effect waves-light btn-sm"
                        @click="
                          openEditDialog(
                            {
                              ...scope.row,
                              label: scope.row.type,
                              value: scope.row.weight,
                              min: scope.row.min_weight,
                              max: scope.row.max_weight,
                            },
                            'thresh',
                            $t('t_clickhouse_threshold_type_weights'),
                          )
                        "
                      >
                        <i class="ri-edit-box-line"></i>
                      </button>
                    </el-tooltip>
                    <el-tooltip
                      effect="light"
                      :content="$t('t_reset_to_default')"
                      placement="top"
                    >
                      <button
                        type="button"
                        class="btn btn-soft-warning waves-effect waves-light btn-sm"
                        @click="handleResetRow(scope.row, 'thresh')"
                      >
                        <i class="ri-refresh-line"></i>
                      </button>
                    </el-tooltip>
                  </div>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <div class="section-subtitle mt-3">
            {{ $t("t_clickhouse_threshold_customer_segmentation_by_score") }}
          </div>
          <div class="table-wrapper">
            <el-table
              :data="userCategoryData"
              border
              style="width: 100%"
              class="modern-table"
            >
              <el-table-column
                :label="$t('t-stt')"
                type="index"
                width="60"
                align="center"
              />
              <el-table-column
                :label="$t('t-type')"
                min-width="150"
                align="center"
              >
                <template #default="scope">
                  <div class="col-tooltip">{{ scope.row.type }}</div>
                </template>
              </el-table-column>
              <el-table-column
                prop="thresh_lower"
                :label="$t('t_thresh_lower')"
                width="130"
                align="center"
              />
              <el-table-column
                prop="thresh_higher"
                :label="$t('t_thresh_higher')"
                width="130"
                align="center"
              />
              <el-table-column
                :label="$t('t_user_type')"
                min-width="300"
                show-overflow-tooltip
                align="center"
              >
                <template #default="scope">
                  <div class="col-tooltip">{{ scope.row.user_type }}</div>
                </template>
              </el-table-column>
              <el-table-column
                :label="$t('t-action')"
                width="120"
                align="center"
              >
                <template #default="scope">
                  <div class="btn-group-thao-tac">
                    <el-tooltip
                      effect="light"
                      :content="$t('action_option')"
                      placement="top"
                    >
                      <button
                        type="button"
                        class="btn btn-soft-success waves-effect waves-light btn-sm"
                        @click="
                          openEditDialog(
                            {
                              ...scope.row,
                              label: scope.row.user_type,
                              value1: scope.row.thresh_lower,
                              value2: scope.row.thresh_higher,
                              min: scope.row.min_thresh,
                              max: scope.row.max_thresh,
                            },
                            'user',
                            $t(
                              't_clickhouse_threshold_customer_segmentation_by_score',
                            ),
                          )
                        "
                      >
                        <i class="ri-edit-box-line"></i>
                      </button>
                    </el-tooltip>
                    <el-tooltip
                      effect="light"
                      :content="$t('t_reset_to_default')"
                      placement="top"
                    >
                      <button
                        type="button"
                        class="btn btn-soft-warning waves-effect waves-light btn-sm"
                        @click="handleResetRow(scope.row, 'user')"
                      >
                        <i class="ri-refresh-line"></i>
                      </button>
                    </el-tooltip>
                  </div>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>

        <div class="tab-card-content mb-4">
          <div class="tab-full-title">
            <i class="ri-settings-4-line align-bottom me-1"></i>
            {{ $t("t_clickhouse_threshold_speed_test") }}
          </div>
          <div class="table-wrapper">
            <el-table
              :data="speedTestData"
              border
              style="width: 100%"
              class="modern-table"
            >
              <el-table-column
                :label="$t('t-stt')"
                type="index"
                width="60"
                align="center"
              />
              <el-table-column
                :label="$t('t-type')"
                min-width="150"
                align="center"
              >
                <template #default="scope">
                  <div class="col-tooltip">{{ scope.row.name }}</div>
                </template>
              </el-table-column>
              <el-table-column
                prop="value"
                :label="$t('t_threshold')"
                width="120"
                align="center"
              />
              <el-table-column
                prop="value_unit"
                :label="$t('t_unit_column')"
                width="100"
                align="center"
              />
              <el-table-column
                :label="$t('t_description_column')"
                min-width="300"
                show-overflow-tooltip
                align="center"
              >
                <template #default="scope">
                  <div class="col-tooltip">{{ scope.row.note }}</div>
                </template>
              </el-table-column>
              <el-table-column
                :label="$t('t-action')"
                width="120"
                align="center"
              >
                <template #default="scope">
                  <div class="btn-group-thao-tac">
                    <el-tooltip
                      effect="light"
                      :content="$t('action_option')"
                      placement="top"
                    >
                      <button
                        type="button"
                        class="btn btn-soft-success waves-effect waves-light btn-sm"
                        @click="
                          openEditDialog(
                            {
                              ...scope.row,
                              label: scope.row.name,
                              value: scope.row.value,
                              min: scope.row.min_value,
                              max: scope.row.max_value,
                            },
                            'speed',
                            $t('t_clickhouse_threshold_speed_test'),
                          )
                        "
                      >
                        <i class="ri-edit-box-line"></i>
                      </button>
                    </el-tooltip>
                    <el-tooltip
                      effect="light"
                      :content="$t('t_reset_to_default')"
                      placement="top"
                    >
                      <button
                        type="button"
                        class="btn btn-soft-warning waves-effect waves-light btn-sm"
                        @click="handleResetRow(scope.row, 'speed')"
                      >
                        <i class="ri-refresh-line"></i>
                      </button>
                    </el-tooltip>
                  </div>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>

        <div class="tab-card-content mb-4">
          <div class="tab-full-title">
            <i class="ri-settings-4-line align-bottom me-1"></i>
            {{ $t("t_clickhouse_time_adjustment_usage") }}
          </div>
          <div class="table-wrapper">
            <el-table
              :data="usageTimeData"
              border
              style="width: 100%"
              class="modern-table"
            >
              <el-table-column
                :label="$t('t-stt')"
                type="index"
                width="60"
                align="center"
              />
              <el-table-column
                :label="$t('t-type')"
                min-width="150"
                align="center"
              >
                <template #default="scope">
                  <div class="col-tooltip">{{ scope.row.name }}</div>
                </template>
              </el-table-column>
              <el-table-column
                prop="value"
                :label="$t('t_threshold')"
                width="120"
                align="center"
              />
              <el-table-column
                prop="value_unit"
                :label="$t('t_unit_column')"
                width="100"
                align="center"
              />
              <el-table-column
                :label="$t('t_description_column')"
                min-width="300"
                show-overflow-tooltip
                align="center"
              >
                <template #default="scope">
                  <div class="col-tooltip">{{ scope.row.note }}</div>
                </template>
              </el-table-column>
              <el-table-column
                :label="$t('t-action')"
                width="120"
                align="center"
              >
                <template #default="scope">
                  <div class="btn-group-thao-tac">
                    <el-tooltip
                      effect="light"
                      :content="$t('action_option')"
                      placement="top"
                    >
                      <button
                        type="button"
                        class="btn btn-soft-success waves-effect waves-light btn-sm"
                        @click="
                          openEditDialog(
                            {
                              ...scope.row,
                              label: scope.row.name,
                              value: scope.row.value,
                              min: scope.row.min_value,
                              max: scope.row.max_value,
                            },
                            'usage',
                            $t('t_clickhouse_time_adjustment_usage'),
                          )
                        "
                      >
                        <i class="ri-edit-box-line"></i>
                      </button>
                    </el-tooltip>
                    <el-tooltip
                      effect="light"
                      :content="$t('t_reset_to_default')"
                      placement="top"
                    >
                      <button
                        type="button"
                        class="btn btn-soft-warning waves-effect waves-light btn-sm"
                        @click="handleResetRow(scope.row, 'usage')"
                      >
                        <i class="ri-refresh-line"></i>
                      </button>
                    </el-tooltip>
                  </div>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>

        <div class="tab-card-content mb-4">
          <div class="tab-full-title">
            <i class="ri-settings-4-line align-bottom me-1"></i>
            {{ $t("t_clickhouse_traffic_threshold_interface") }}
          </div>
          <div class="table-wrapper">
            <el-table
              :data="trafficInterfaceData"
              border
              style="width: 100%"
              class="modern-table"
            >
              <el-table-column
                :label="$t('t-stt')"
                type="index"
                width="60"
                align="center"
              />
              <el-table-column
                :label="$t('t-type')"
                min-width="150"
                align="center"
              >
                <template #default="scope">
                  <div class="col-tooltip">{{ scope.row.name }}</div>
                </template>
              </el-table-column>
              <el-table-column
                prop="value"
                :label="$t('t_threshold')"
                width="120"
                align="center"
              />
              <el-table-column
                prop="value_unit"
                :label="$t('t_unit_column')"
                width="100"
                align="center"
              />
              <el-table-column
                :label="$t('t_description_column')"
                min-width="300"
                show-overflow-tooltip
                align="center"
              >
                <template #default="scope">
                  <div class="col-tooltip">{{ scope.row.note }}</div>
                </template>
              </el-table-column>
              <el-table-column
                :label="$t('t-action')"
                width="120"
                align="center"
              >
                <template #default="scope">
                  <div class="btn-group-thao-tac">
                    <el-tooltip
                      effect="light"
                      :content="$t('action_option')"
                      placement="top"
                    >
                      <button
                        type="button"
                        class="btn btn-soft-success waves-effect waves-light btn-sm"
                        @click="
                          openEditDialog(
                            {
                              ...scope.row,
                              label: scope.row.name,
                              value: scope.row.value,
                              min: scope.row.min_value,
                              max: scope.row.max_value,
                            },
                            'traffic',
                            $t('t_clickhouse_traffic_threshold_interface'),
                          )
                        "
                      >
                        <i class="ri-edit-box-line"></i>
                      </button>
                    </el-tooltip>
                    <el-tooltip
                      effect="light"
                      :content="$t('t_reset_to_default')"
                      placement="top"
                    >
                      <button
                        type="button"
                        class="btn btn-soft-warning waves-effect waves-light btn-sm"
                        @click="handleResetRow(scope.row, 'traffic')"
                      >
                        <i class="ri-refresh-line"></i>
                      </button>
                    </el-tooltip>
                  </div>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>

        <div class="tab-card-content mb-4">
          <div class="tab-full-title">
            <i class="ri-settings-4-line align-bottom me-1"></i>
            {{ $t("t_clickhouse_qoe_weight") }}
          </div>
          <div class="table-wrapper">
            <el-table
              :data="qoeTableData"
              border
              style="width: 100%"
              class="modern-table"
            >
              <el-table-column :label="$t('t-stt')" width="60" align="center">
                <template #default="scope">
                  <div class="text-center">
                    {{ qoeOffset + scope.$index + 1 }}
                  </div>
                </template>
              </el-table-column>
              <el-table-column
                :label="$t('t-type')"
                min-width="150"
                align="center"
              >
                <template #default="scope">
                  <div class="col-tooltip">{{ scope.row.type }}</div>
                </template>
              </el-table-column>
              <el-table-column
                :label="$t('t_qoe_name')"
                min-width="150"
                align="center"
              >
                <template #default="scope">
                  <div class="col-tooltip">{{ scope.row.name }}</div>
                </template>
              </el-table-column>
              <el-table-column
                prop="weight"
                :label="$t('t_weight')"
                width="120"
                align="center"
              />
              <el-table-column
                :label="$t('t_description_column')"
                min-width="300"
                show-overflow-tooltip
                align="center"
              >
                <template #default="scope">
                  <div class="col-tooltip">{{ scope.row.description }}</div>
                </template>
              </el-table-column>
              <el-table-column
                :label="$t('t-action')"
                width="120"
                align="center"
              >
                <template #default="scope">
                  <div class="btn-group-thao-tac">
                    <el-tooltip
                      effect="light"
                      :content="$t('action_option')"
                      placement="top"
                    >
                      <button
                        type="button"
                        class="btn btn-soft-success waves-effect waves-light btn-sm"
                        :disabled="!scope.row.service_id"
                        @click="
                          openEditDialog(
                            {
                              ...scope.row,
                              label: scope.row.name,
                              service_id: scope.row.service_id,
                              value: scope.row.weight,
                              min: scope.row.min_weight,
                              max: scope.row.max_weight,
                            },
                            'qoe',
                            $t('t_clickhouse_qoe_weight'),
                          )
                        "
                      >
                        <i class="ri-edit-box-line"></i>
                      </button>
                    </el-tooltip>
                    <el-tooltip
                      effect="light"
                      :content="$t('t_reset_to_default')"
                      placement="top"
                    >
                      <button
                        type="button"
                        class="btn btn-soft-warning waves-effect waves-light btn-sm"
                        :disabled="!scope.row.service_id"
                        @click="handleResetRow(scope.row, 'qoe')"
                      >
                        <i class="ri-refresh-line"></i>
                      </button>
                    </el-tooltip>
                  </div>
                </template>
              </el-table-column>
            </el-table>
          </div>
          <div class="pagination-wrapper mt-3">
            <el-pagination
              small
              background
              v-model:current-page="qoePage"
              v-model:page-size="qoePageSize"
              :total="qoeTotal"
              layout="total, sizes, prev, pager, next"
              :page-sizes="[10, 20, 50]"
            />
          </div>
        </div>
      </div>
    </div>

    <el-dialog
      v-model="showEditDialog"
      :title="$t('t_update')"
      width="500"
      destroy-on-close
      align-center
    >
      <el-form label-position="top">
        <el-form-item
          :label="
            editingItem.tableTitle +
            ' ' +
            (editingItem.type_table === 'speed' ||
            editingItem.type_table === 'usage'
              ? ''
              : editingItem.label)
          "
        >
          <template v-if="editingItem.type_table === 'user'">
            <div class="row w-100">
              <div class="col-6">
                <div class="d-flex align-items-center gap-2">
                  <span class="text-nowrap">Min</span>
                  <el-input v-model="editingItem.value1" type="number" />
                </div>
              </div>
              <div class="col-6">
                <div class="d-flex align-items-center gap-2">
                  <span class="text-nowrap">Max</span>
                  <el-input v-model="editingItem.value2" type="number" />
                </div>
              </div>
              <div
                v-if="
                  isNaNValue(editingItem.value1) ||
                  isNaNValue(editingItem.value2)
                "
                class="col-12 text-danger mt-1"
                style="font-size: 12px"
              >
                {{ $t("t_validate_number_only") }}
              </div>
              <div
                v-if="
                  (editingItem.min != null &&
                    editingItem.max != null &&
                    isOutOfRange(
                      editingItem.value1,
                      editingItem.min,
                      editingItem.max,
                    )) ||
                  isOutOfRange(
                    editingItem.value2,
                    editingItem.min,
                    editingItem.max,
                  )
                "
                class="col-12 text-danger mt-1"
                style="font-size: 12px"
              >
                {{ $t("t_validate_value_input") }} {{ editingItem.min }}
                {{ $t("t_to_lowercase") }} {{ editingItem.max }}
              </div>
              <div
                v-if="
                  isMinGreaterThanMax(editingItem.value1, editingItem.value2)
                "
                class="col-12 text-danger mt-1"
                style="font-size: 12px"
              >
                {{ $t("t_validate_min_greater_max") }}
              </div>
            </div>
          </template>
          <template v-else>
            <el-input v-model="editingItem.value" type="number">
              <template v-if="editingItem.value_unit" #append>{{
                editingItem.value_unit
              }}</template>
            </el-input>
            <div
              v-if="isNaNValue(editingItem.value)"
              class="text-danger mt-1"
              style="font-size: 12px"
            >
              {{ $t("t_validate_number_only") }}
            </div>
            <div
              v-if="
                editingItem.min != null &&
                editingItem.max != null &&
                isOutOfRange(
                  editingItem.value,
                  editingItem.min,
                  editingItem.max,
                )
              "
              class="text-danger mt-1"
              style="font-size: 12px"
            >
              {{ $t("t_validate_value_input") }} {{ editingItem.min }}
              {{ $t("t_to_lowercase") }} {{ editingItem.max }}
            </div>
          </template>
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <button
            type="button"
            class="btn btn-secondary bg-gradient btn-border waves-effect waves-light"
            @click="handleUpdateCategory"
          >
            {{ $t("t-update") }}
          </button>
        </div>
      </template>
    </el-dialog>
  </Layout>
</template>

<style lang="scss" scoped>
@import "./cau-hinh-clickhouse.scss";
</style>
